import React, { useState, useEffect } from 'react';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import { loadGoogleFonts } from '../utils/fonts';
import AppHeader from '../components/AppHeader';
import MobileDrawer from '../components/MobileDrawer';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Select,
  InputLabel,
  Slider,
  MenuItem,
  FormControl,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  ThemeProvider,
  createTheme,
  Stack,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Fade,
  Backdrop
} from '@mui/material';

import {
  ShoppingCart,
  Menu,
  Home,
  Store,
  Calculate,
  Info,
  ContactMail,
  WbSunny,
  Search,
  FilterList,
  Share,
  AddShoppingCart,
  RemoveShoppingCart,
  LocalShipping,
  Security,
  KeyboardArrowRight,
  Phone,
  Assessment,
  CardGiftcard,
  School,
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Person,
  Close,
  Google,
  Facebook
} from '@mui/icons-material';
import axios from 'axios';
import AppFooter from '../components/AppFooter';
import AuthModal from '../components/AuthModal';

const BASE_URL = 'https://dhahabjw.com';

// Product List Component
const ProductList = () => {
  const [searchParams] = useSearchParams(); // Add this
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addingToCart, setAddingToCart] = useState({});
  const [removingFromCart, setRemovingFromCart] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [pendingCartProduct, setPendingCartProduct] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // ----- Filters -----
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMetal, setSelectedMetal] = useState('all');   // all | gold | silver
  const [selectedKarat, setSelectedKarat] = useState('all');   // all | 18K | 21K | 22K | 24K | 925
  const [weightRange, setWeightRange] = useState(null); // [min,max] or null (= full range)
  const [priceRange, setPriceRange] = useState(null);   // [min,max] or null (= full range)
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured'); // featured | price_asc | price_desc | weight_asc | weight_desc

  const karatOptions = ['18K', '21K', '22K', '24K', '925'];

  const resetFilters = () => {
    setSelectedCategory('all');
    setSelectedMetal('all');
    setSelectedKarat('all');
    setWeightRange(null);
    setPriceRange(null);
    setInStockOnly(false);
    setSortBy('featured');
    setSearchTerm('');
  };

  // Weights to consider for a product (its size variants, else its base weight)
  const productWeights = (product) => {
    const sizes = (product.sizes || []).filter(s => s.isActive !== false && s.weight > 0);
    if (sizes.length > 0) return sizes.map(s => Number(s.weight));
    return product.weight ? [Number(product.weight)] : [];
  };

  // Slider bounds derived from the loaded products.
  const allWeights = products.flatMap(productWeights);
  const allPrices = products.map(p => Number(p.pricing?.finalPrice || 0));
  const weightBounds = [0, Math.max(1, Math.ceil(Math.max(0, ...allWeights)))];
  const priceBounds = [0, Math.max(1, Math.ceil(Math.max(0, ...allPrices)))];
  const effWeightRange = weightRange || weightBounds;
  const effPriceRange = priceRange || priceBounds;
  const weightStep = weightBounds[1] <= 20 ? 0.5 : 1;
  const priceStep = priceBounds[1] <= 50 ? 0.5 : 1;

  const weightActive = !!weightRange &&
    (weightRange[0] > weightBounds[0] || weightRange[1] < weightBounds[1]);
  const priceActive = !!priceRange &&
    (priceRange[0] > priceBounds[0] || priceRange[1] < priceBounds[1]);

  const activeFilterCount = [
    selectedCategory !== 'all',
    selectedMetal !== 'all',
    selectedKarat !== 'all',
    weightActive,
    priceActive,
    inStockOnly,
  ].filter(Boolean).length;

  // Update useEffect to check for category parameter
  useEffect(() => {
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams]);
  // Fetch categories
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCategories(data.data || []);
      } else {
        console.error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Check if user is logged in on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (token && userData) {
      setUser(JSON.parse(userData));
      fetchCartItems();
    }
  }, []);

  // Fetch cart items
  const fetchCartItems = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('https://dhahabjw.com/api/cart', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCartItems(response.data.data.items || []);
      }
    } catch (error) {
      console.error('Error fetching cart items:', error);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(`https://dhahabjw.com/api/products?lang=${language}&limit=1000`);
        setProducts(data.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
    fetchProducts();
  }, [language]);

  const isProductInCart = (productId) => {
    return cartItems.some(item => item.product.id === productId);
  };

  const getCartItemForProduct = (productId) => {
    return cartItems.find(item => item.product.id === productId);
  };

  const getLocalizedName = (product) => {
    if (typeof product.name === 'object' && product.name !== null) {
      return product.name[language] || product.name.en || '';
    }
    return product.name || '';
  };

  const getLocalizedDescription = (product) => {
    if (typeof product.description === 'object' && product.description !== null) {
      return product.description[language] || product.description.en || '';
    }
    return product.description || '';
  };

  const getLocalizedCategoryName = (category) => {
    if (typeof category.name === 'object' && category.name !== null) {
      return category.name[language] || category.name.en || '';
    }
    return category.name || '';
  };

  const handleAddToCart = async (productId, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token || !user) {
      setPendingCartProduct(productId);
      setAuthModalOpen(true);
      return;
    }

    try {
      setAddingToCart(prev => ({ ...prev, [productId]: true }));
      const response = await axios.post(
        'https://dhahabjw.com/api/cart/add',
        {
          productId: productId,
          quantity: 1
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: t.addedToCart || 'Product added to cart successfully!',
          severity: 'success'
        });
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { itemCount: response.data.data?.cartItemCount }
        }));
        await fetchCartItems();
      } else {
        throw new Error(response.data.error || 'Failed to add to cart');
      }
    } catch (error) {
      let errorMessage = t.addToCartError || 'Failed to add product to cart';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = t.loginRequired || 'Please login to add items to cart';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setPendingCartProduct(productId);
        setAuthModalOpen(true);
        return;
      } else if (error.response?.status === 404) {
        errorMessage = t.productNotFound || 'Product not found or unavailable';
      } else if (error.response?.status === 400) {
        errorMessage = t.invalidRequest || 'Invalid request';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = t.timeout || 'Request timeout. Please try again.';
      } else if (!error.response) {
        errorMessage = t.networkError || 'Network error. Please check your connection.';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setAddingToCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleRemoveFromCart = async (productId, e) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token || !user) {
      setSnackbar({
        open: true,
        message: t.loginRequired || 'Please login to remove items from cart',
        severity: 'error'
      });
      return;
    }

    const cartItem = getCartItemForProduct(productId);
    if (!cartItem) {
      setSnackbar({
        open: true,
        message: t.itemNotFound || 'Item not found in cart',
        severity: 'error'
      });
      return;
    }

    try {
      setRemovingFromCart(prev => ({ ...prev, [productId]: true }));
      const response = await axios.delete(
        `https://dhahabjw.com/api/cart/remove/${cartItem.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        }
      );

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: t.removedFromCart || 'Product removed from cart successfully!',
          severity: 'success'
        });
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { itemCount: cartItems.length - 1 }
        }));
        await fetchCartItems();
      }
    } catch (error) {
      let errorMessage = t.removeFromCartError || 'Failed to remove product from cart';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMessage = t.loginRequired || 'Please login to remove items from cart';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      } else if (error.response?.status === 404) {
        errorMessage = t.itemNotFound || 'Item not found in cart';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = t.timeout || 'Request timeout. Please try again.';
      } else if (!error.response) {
        errorMessage = t.networkError || 'Network error. Please check your connection.';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setRemovingFromCart(prev => ({ ...prev, [productId]: false }));
    }
  };

  const handleLoginSuccess = async () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    await fetchCartItems();

    if (pendingCartProduct) {
      setTimeout(async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            console.error('No token found after login');
            return;
          }

          setAddingToCart(prev => ({ ...prev, [pendingCartProduct]: true }));
          const response = await axios.post(
            'https://dhahabjw.com/api/cart/add',
            {
              productId: pendingCartProduct,
              quantity: 1
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              timeout: 10000
            }
          );

          if (response.data.success) {
            setSnackbar({
              open: true,
              message: response.data.message || t.addedToCart || 'Product added to cart successfully!',
              severity: 'success'
            });
            window.dispatchEvent(new CustomEvent('cartUpdated', {
              detail: { itemCount: response.data.data?.cartItemCount }
            }));
            await fetchCartItems();
          }
        } catch (error) {
          setSnackbar({
            open: true,
            message: error.response?.data?.error || t.addToCartError || 'Failed to add product to cart',
            severity: 'error'
          });
        } finally {
          setAddingToCart(prev => ({ ...prev, [pendingCartProduct]: false }));
          setPendingCartProduct(null);
        }
      }, 1000);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
    setPendingCartProduct(null);
  };

  const filteredProducts = products.filter(product => {
    const name = getLocalizedName(product).toLowerCase();
    const category = (product.category || '').toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();

    const matchesSearch = name.includes(searchTermLower) || category.includes(searchTermLower);
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;

    // Metal (type)
    const matchesMetal = selectedMetal === 'all' ||
      (product.metal || '').toLowerCase() === selectedMetal;

    // Karat
    const matchesKarat = selectedKarat === 'all' ||
      String(product.karat || '').toLowerCase() === selectedKarat.toLowerCase();

    // Weight range (matches if any of the product's weights fall within range)
    const weights = productWeights(product);
    const [minW, maxW] = effWeightRange;
    const matchesWeight = !weightActive || (weights.length === 0
      ? false
      : weights.some(w => w >= minW && w <= maxW));

    // Price range (on base final price)
    const price = Number(product.pricing?.finalPrice || 0);
    const [minP, maxP] = effPriceRange;
    const matchesPrice = !priceActive || (price >= minP && price <= maxP);

    // In stock
    const matchesStock = !inStockOnly || product.inventory?.inStock === true;

    return matchesSearch && matchesCategory && matchesMetal && matchesKarat &&
      matchesWeight && matchesPrice && matchesStock;
  }).sort((a, b) => {
    const priceA = Number(a.pricing?.finalPrice || 0);
    const priceB = Number(b.pricing?.finalPrice || 0);
    const weightA = Math.min(...(productWeights(a).length ? productWeights(a) : [0]));
    const weightB = Math.min(...(productWeights(b).length ? productWeights(b) : [0]));
    switch (sortBy) {
      case 'price_asc': return priceA - priceB;
      case 'price_desc': return priceB - priceA;
      case 'weight_asc': return weightA - weightB;
      case 'weight_desc': return weightB - weightA;
      default: return 0;
    }
  });

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
          <AppHeader language={language} setLanguage={() => { }} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Box textAlign="center" px={2}>
              <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
              <Typography color="text.secondary" variant="body1">
                {t.loading}
              </Typography>
            </Box>
          </Box>
          <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
        <AppHeader
          language={language}
          setLanguage={setLanguage}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          t={t}
        />

        <MobileDrawer
          language={language}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          t={t}
        />

        <Container
          maxWidth="xl"
          sx={{
            py: { xs: 2, sm: 3, md: 4 },
            px: { xs: 1, sm: 2, md: 3 }
          }}
        >
          {/* Header with Search, Category Filter, and Mobile Optimization */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              mb: { xs: 2, sm: 3, md: 4 },
              gap: { xs: 2, sm: 0 }
            }}
          >
            <Typography
              variant="h4"
              color="primary"
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              {t.store}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 2 },
                alignItems: 'center',
                width: { xs: '100%', sm: 'auto' },
                flexWrap: 'wrap'
              }}
            >
              <TextField
                placeholder={t.search}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 250 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(26, 26, 26, 0.8)',
                    borderRadius: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl
                size="small"
                sx={{
                  width: { xs: '100%', sm: 200 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(26, 26, 26, 0.8)',
                    borderRadius: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
              >
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  displayEmpty
                  disabled={categoriesLoading}
                  sx={{
                    bgcolor: 'rgba(26, 26, 26, 0.8)',
                    color: 'text.primary',
                    '& .MuiSelect-icon': {
                      color: 'text.secondary'
                    }
                  }}
                >
                  <MenuItem value="all">{t.allCategories || 'All Categories'}</MenuItem>
                  {availableCategories.map(category => (
                    <MenuItem key={category._id} value={category._id}>
                      {getLocalizedCategoryName(category)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                onClick={() => setShowFilters(prev => !prev)}
                startIcon={<FilterList />}
                sx={{
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  color: showFilters ? 'black' : 'text.primary',
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {t.filters || 'Filters'}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            </Box>
          </Box>

          {/* ----- Filter panel ----- */}
          {showFilters && (
            <Box
              sx={{
                mb: { xs: 2, sm: 3 },
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                bgcolor: 'rgba(26, 26, 26, 0.8)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <Grid container spacing={2}>
                {/* Metal / Type */}
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t.metal || 'Metal'}</InputLabel>
                    <Select
                      label={t.metal || 'Metal'}
                      value={selectedMetal}
                      onChange={(e) => setSelectedMetal(e.target.value)}
                    >
                      <MenuItem value="all">{t.all || 'All'}</MenuItem>
                      <MenuItem value="gold">{t.gold || 'Gold'}</MenuItem>
                      <MenuItem value="silver">{t.silver || 'Silver'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Karat */}
                <Grid item xs={6} sm={4} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t.karat || 'Karat'}</InputLabel>
                    <Select
                      label={t.karat || 'Karat'}
                      value={selectedKarat}
                      onChange={(e) => setSelectedKarat(e.target.value)}
                    >
                      <MenuItem value="all">{t.all || 'All'}</MenuItem>
                      {karatOptions.map(k => (
                        <MenuItem key={k} value={k}>{k}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Weight range slider */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t.weight || 'Weight'} (g): {effWeightRange[0]} – {effWeightRange[1]}
                  </Typography>
                  <Box sx={{ px: 1 }}>
                    <Slider
                      value={effWeightRange}
                      min={weightBounds[0]}
                      max={weightBounds[1]}
                      step={weightStep}
                      onChange={(e, val) => setWeightRange(val)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v}g`}
                      sx={{ color: 'primary.main' }}
                    />
                  </Box>
                </Grid>

                {/* Price range slider */}
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t.price || 'Price'} ({t.kwd || 'KWD'}): {effPriceRange[0]} – {effPriceRange[1]}
                  </Typography>
                  <Box sx={{ px: 1 }}>
                    <Slider
                      value={effPriceRange}
                      min={priceBounds[0]}
                      max={priceBounds[1]}
                      step={priceStep}
                      onChange={(e, val) => setPriceRange(val)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v} ${t.kwd || 'KWD'}`}
                      sx={{ color: 'primary.main' }}
                    />
                  </Box>
                </Grid>

                {/* Sort */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t.sortBy || 'Sort by'}</InputLabel>
                    <Select
                      label={t.sortBy || 'Sort by'}
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <MenuItem value="featured">{t.featured || 'Featured'}</MenuItem>
                      <MenuItem value="price_asc">{t.priceLowHigh || 'Price: Low to High'}</MenuItem>
                      <MenuItem value="price_desc">{t.priceHighLow || 'Price: High to Low'}</MenuItem>
                      <MenuItem value="weight_asc">{t.weightLowHigh || 'Weight: Low to High'}</MenuItem>
                      <MenuItem value="weight_desc">{t.weightHighLow || 'Weight: High to Low'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* In stock */}
                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inStockOnly}
                        onChange={(e) => setInStockOnly(e.target.checked)}
                        sx={{ color: 'primary.main' }}
                      />
                    }
                    label={t.inStockOnly || 'In stock only'}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredProducts.length} {filteredProducts.length === 1 ? (t.item || 'item') : (t.items || 'items')}
                </Typography>
                <Button
                  variant="text"
                  onClick={resetFilters}
                  disabled={activeFilterCount === 0 && !searchTerm && sortBy === 'featured'}
                  sx={{ color: 'primary.main' }}
                >
                  {t.clearFilters || 'Clear all'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Products Grid */}
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} alignItems="stretch">
            {filteredProducts.map(product => {
              const isInCart = isProductInCart(product.id);
              const isProcessing = addingToCart[product.id] || removingFromCart[product.id];
              const category = availableCategories.find((item) => item._id === product.category);
              return (
                <Grid item xs={6} sm={6} md={4} lg={3} key={product.id}>
                  <Card
                    onClick={() => navigate(`/store/${product.id}`)}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                        boxShadow: { xs: 1, sm: 4 }
                      },
                      '&:active': {
                        transform: 'scale(0.98)'
                      }
                    }}
                  >
                    <Box sx={{
                      position: 'relative',
                      height: { xs: '140px', sm: '180px', md: '200px' },
                      overflow: 'hidden'
                    }}>
                      <CardMedia
                        component="img"
                        height="100%"
                        image={product.images?.find(img => img.isPrimary)?.url || product.images?.[0]?.url}
                        alt={getLocalizedName(product)}
                        sx={{
                          objectFit: 'cover',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                      {product.inventory?.quantity < 1 && (
                        <Chip
                          label={t.outOfStock}
                          color="error"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: { xs: 4, sm: 8 },
                            left: { xs: 4, sm: 8 },
                            fontSize: { xs: '0.625rem', sm: '0.75rem' }
                          }}
                        />
                      )}
                      {isInCart && (
                        <Chip
                          label={t.inCart || 'In Cart'}
                          color="success"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: { xs: 4, sm: 8 },
                            right: { xs: 4, sm: 8 },
                            fontSize: { xs: '0.625rem', sm: '0.75rem' }
                          }}
                        />
                      )}
                    </Box>

                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: { xs: 1, sm: 1.5, md: 2 },
                        '&:last-child': { pb: { xs: 1, sm: 1.5, md: 2 } }
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: { xs: 0.5, sm: 1 },
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                          lineHeight: { xs: 1.2, sm: 1.3 },
                          minHeight: { xs: '2rem', sm: '2.5rem' },
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {getLocalizedName(product)}
                      </Typography>

                      <Box
                        display="flex"
                        gap={0.5}
                        mb={{ xs: 1, sm: 1.5, md: 2 }}
                        sx={{
                          minHeight: { xs: '1.5rem', sm: '2rem' },
                          flexWrap: 'wrap'
                        }}
                      >
                        <Chip
                          label={category ? getLocalizedCategoryName(category) : ''}
                          size="small"
                          color="secondary"
                          sx={{
                            fontSize: { xs: '0.625rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' }
                          }}
                        />
                        <Chip
                          label={product.karat}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: { xs: '0.625rem', sm: '0.75rem' },
                            height: { xs: '20px', sm: '24px' }
                          }}
                        />
                      </Box>

                      <Box mt="auto">
                        <Typography
                          variant="h6"
                          color="primary"
                          sx={{
                            fontWeight: 600,
                            mb: { xs: 0.5, sm: 1 },
                            fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                            minHeight: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                          }}
                        >
                          {product.pricing?.finalPrice?.toFixed(3) || '0.000'} {t.kwd}
                        </Typography>

                        {isInCart ? (
                          <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            size="small"
                            startIcon={
                              removingFromCart[product.id] ?
                                <CircularProgress size={14} color="inherit" /> :
                                <RemoveShoppingCart sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                            }
                            disabled={isProcessing}
                            onClick={(e) => handleRemoveFromCart(product.id, e)}
                            sx={{
                              minHeight: { xs: '32px', sm: '36px', md: '42px' },
                              fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.875rem' },
                              px: { xs: 1, sm: 2 },
                              borderColor: 'error.main',
                              '&:hover': {
                                borderColor: 'error.dark',
                                bgcolor: 'error.main',
                                color: 'white'
                              }
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: { xs: 'none', sm: 'inline' }
                              }}
                            >
                              {removingFromCart[product.id] ? (t.removing || 'Removing...') : (t.removeFromCart || 'Remove from Cart')}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                display: { xs: 'inline', sm: 'none' }
                              }}
                            >
                              {removingFromCart[product.id] ? (t.removing || 'Removing...') : (t.remove || 'Remove')}
                            </Box>
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            fullWidth
                            size="small"
                            startIcon={
                              addingToCart[product.id] ?
                                <CircularProgress size={14} color="inherit" /> :
                                <AddShoppingCart sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                            }
                            disabled={product.inventory?.quantity < 1 || isProcessing}
                            onClick={(e) => handleAddToCart(product.id, e)}
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'black',
                              '&:hover': { bgcolor: 'primary.dark' },
                              minHeight: { xs: '32px', sm: '36px', md: '42px' },
                              fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.875rem' },
                              px: { xs: 1, sm: 2 }
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: { xs: 'none', sm: 'inline' }
                              }}
                            >
                              {addingToCart[product.id] ? (t.adding || 'Adding...') : t.addToCart}
                            </Box>
                            <Box
                              component="span"
                              sx={{
                                display: { xs: 'inline', sm: 'none' }
                              }}
                            >
                              {addingToCart[product.id] ? (t.adding || 'Adding...') : (t.add || 'Add')}
                            </Box>
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {filteredProducts.length === 0 && (
            <Box textAlign="center" py={{ xs: 4, sm: 6, md: 8 }} px={2}>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {t.noProductsFound || 'No products found'}
              </Typography>
            </Box>
          )}
        </Container>

        <AuthModal
          open={authModalOpen}
          onClose={handleAuthModalClose}
          language={language}
          t={t}
          onLoginSuccess={handleLoginSuccess}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          sx={{
            '& .MuiSnackbarContent-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              maxWidth: { xs: '90vw', sm: 'auto' }
            }
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <AppFooter t={t} />
      </Box>
    </ThemeProvider>
  );
};

export default ProductList;